import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "../runtime";
import type {
  AiRunFilters,
  AnalyticsSection,
  AppointmentFilters,
  AuditFilters,
  ConversationFilters,
  DateRange,
  ErrorFilters,
  JobFilters,
  ListQuery,
  OfferRunFilters,
  OpportunityFilters,
  OrganizationFilters,
  PurchaseFilters,
  StaffFilters,
  StoreFilters,
  UserFilters,
} from "../types";
import { qk } from "./keys";

/**
 * Domain query hooks. Components use these — never adapters or URLs. Each calls the runtime's
 * adapter (HTTP when the contract defines the operation, otherwise a typed
 * ContractNotImplementedError; mock adapters in mock mode).
 */
const list = { placeholderData: keepPreviousData } as const;

// ── session ──────────────────────────────────────────────────────────────
export const currentAdminQuery = () => queryOptions({ queryKey: qk.session(), queryFn: () => api.session.current(), staleTime: 60_000 });

// ── system ───────────────────────────────────────────────────────────────
export const useSystemHealth = () => useQuery({ queryKey: qk.system.health(), queryFn: () => api.system.health(), refetchInterval: 30_000, staleTime: 10_000 });
export const useOverviewMetrics = () => useQuery({ queryKey: qk.system.overview(), queryFn: () => api.system.overview(), refetchInterval: 60_000 });
export const useSystemMetrics = () => useQuery({ queryKey: qk.system.metrics(), queryFn: () => api.system.metrics(), refetchInterval: 60_000 });
export const useErrors = (q: ListQuery<ErrorFilters>) => useQuery({ queryKey: qk.system.errors(q), queryFn: () => api.system.errors(q), ...list });
export const useSystemEvents = (q: ListQuery) => useQuery({ queryKey: qk.system.events(q), queryFn: () => api.system.events(q), ...list });

// ── platform ─────────────────────────────────────────────────────────────
export const useOrganizations = (q: ListQuery<OrganizationFilters>) =>
  useQuery({ queryKey: qk.organizations.list(q), queryFn: () => api.organizations.list(q), ...list });
export const useOrganization = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.organizations.detail(id ?? "none"), queryFn: () => api.organizations.get(id as string), enabled: Boolean(id) });
export const useStores = (q: ListQuery<StoreFilters>, enabled = true) =>
  useQuery({ queryKey: qk.stores.list(q), queryFn: () => api.stores.list(q), enabled, ...list });
export const useStore = (id: string) => useQuery({ queryKey: qk.stores.detail(id), queryFn: () => api.stores.get(id) });
export const useUsers = (q: ListQuery<UserFilters>) => useQuery({ queryKey: qk.users.list(q), queryFn: () => api.users.list(q), ...list });
export const useUser = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.users.detail(id ?? "none"), queryFn: () => api.users.get(id as string), enabled: Boolean(id) });

// ── operations ───────────────────────────────────────────────────────────
export const useOpportunities = (q: ListQuery<OpportunityFilters>) =>
  useQuery({ queryKey: qk.opportunities.list(q), queryFn: () => api.opportunities.list(q), ...list });
export const useOpportunity = (id: string) => useQuery({ queryKey: qk.opportunities.detail(id), queryFn: () => api.opportunities.get(id) });
export const useOpportunityTimeline = (id: string) => useQuery({ queryKey: qk.opportunities.timeline(id), queryFn: () => api.opportunities.timeline(id) });
export const useConversations = (q: ListQuery<ConversationFilters>) =>
  useQuery({ queryKey: qk.conversations.list(q), queryFn: () => api.conversations.list(q), ...list });
export const useConversation = (id: string) => useQuery({ queryKey: qk.conversations.detail(id), queryFn: () => api.conversations.get(id) });
export const useConversationMessages = (id: string, opts: { refetchInterval?: number | false } = {}) =>
  useQuery({ queryKey: qk.conversations.messages(id), queryFn: () => api.conversations.messages(id), refetchInterval: opts.refetchInterval });
export const useOpportunityNotes = (id: string) => useQuery({ queryKey: qk.opportunities.notes(id), queryFn: () => api.opportunities.notes(id) });
export const useOpportunityImages = (id: string) =>
  // Image URLs are signed for an hour; refetch well before they expire.
  useQuery({ queryKey: qk.opportunities.images(id), queryFn: () => api.opportunities.images(id), staleTime: 5 * 60_000 });
/** Development only: callers check AdminIdentity.devTools first. */
export const useSmsSimulator = (conversationId: string, enabled: boolean) =>
  useQuery({ queryKey: qk.conversations.simulator(conversationId), queryFn: () => api.dev.smsSimulator(conversationId), enabled });
/** A message photo's short-lived URL (refreshed before it expires). */
export const useMessageMedia = (messageId: string, index: number) =>
  useQuery({
    queryKey: qk.conversations.media(messageId, index),
    queryFn: () => api.conversations.media(messageId, index),
    staleTime: 10 * 60_000,
    retry: false,
  });
export const useConversationTimeline = (id: string) => useQuery({ queryKey: qk.conversations.timeline(id), queryFn: () => api.conversations.timeline(id) });
export const useAppointments = (q: ListQuery<AppointmentFilters>) =>
  useQuery({ queryKey: qk.appointments.list(q), queryFn: () => api.appointments.list(q), ...list });
export const usePurchases = (q: ListQuery<PurchaseFilters>) => useQuery({ queryKey: qk.purchases.list(q), queryFn: () => api.purchases.list(q), ...list });
export const usePurchaseTrace = (id: string | null) =>
  useQuery({ queryKey: qk.purchases.trace(id ?? "none"), queryFn: () => api.purchases.trace(id as string), enabled: Boolean(id) });

// ── AI & automation ──────────────────────────────────────────────────────
export const useAiSummary = (range: DateRange) => useQuery({ queryKey: qk.ai.summary(range), queryFn: () => api.ai.summary(range), ...list });
export const useAiRuns = (q: ListQuery<AiRunFilters>) => useQuery({ queryKey: qk.ai.runs(q), queryFn: () => api.ai.listRuns(q), ...list });
export const useAiRun = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.ai.run(id ?? "none"), queryFn: () => api.ai.getRun(id as string), enabled: Boolean(id) });
export const useQueues = () => useQuery({ queryKey: qk.queues.list(), queryFn: () => api.queues.list(), refetchInterval: 15_000 });
export const useQueue = (name: string) => useQuery({ queryKey: qk.queues.detail(name), queryFn: () => api.queues.get(name), refetchInterval: 15_000 });
export const useQueueJobs = (name: string, q: ListQuery<JobFilters>) =>
  useQuery({ queryKey: qk.queues.jobs(name, q), queryFn: () => api.queues.listJobs(name, q), ...list });
export const useQueueJob = (name: string, id: string | null | undefined) =>
  useQuery({ queryKey: qk.queues.job(name, id ?? "none"), queryFn: () => api.queues.getJob(name, id as string), enabled: Boolean(id) });

// ── integrations / insights / security ───────────────────────────────────
export const useIntegrations = () => useQuery({ queryKey: qk.integrations.list(), queryFn: () => api.integrations.list(), refetchInterval: 60_000 });
export const useIntegration = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.integrations.detail(id), queryFn: () => api.integrations.get(id), enabled });
/** Every offer API integration (to know which organizations already have one). */
export const useOfferIntegrations = () => useQuery({ queryKey: qk.integrations.offerList(), queryFn: () => api.offerApi.list() });
export const useOfferDrivers = () => useQuery({ queryKey: qk.integrations.offerDrivers(), queryFn: () => api.offerApi.drivers(), staleTime: 5 * 60_000 });
export const useOfferIntegration = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.integrations.offerDetail(id), queryFn: () => api.offerApi.get(id), enabled });
export const useOfferRuns = (integrationId: string, q: ListQuery<OfferRunFilters>) =>
  useQuery({ queryKey: qk.integrations.offerRuns(integrationId, q), queryFn: () => api.offerApi.runs(integrationId, q), ...list });
/** One lead's Offer API log (refreshes while a request is running). */
export const useOpportunityOfferRuns = (opportunityId: string, q: ListQuery<OfferRunFilters>) =>
  useQuery({
    queryKey: qk.integrations.opportunityOfferRuns(opportunityId, q),
    queryFn: () => api.offerApi.opportunityRuns(opportunityId, q),
    refetchInterval: (query) => (query.state.data?.pending ? 5_000 : false),
  });
export const useOfferRun = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.integrations.offerRun(id ?? "none"), queryFn: () => api.offerApi.run(id as string), enabled: Boolean(id) });
export const useAnalyticsReport = (section: AnalyticsSection, range: DateRange) =>
  useQuery({ queryKey: qk.analytics(section, range), queryFn: () => api.analytics.report(section, range), ...list });
export const useAuditEvents = (q: ListQuery<AuditFilters>) => useQuery({ queryKey: qk.audit.list(q), queryFn: () => api.audit.list(q), ...list });
export const useAuditEvent = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.audit.detail(id ?? "none"), queryFn: () => api.audit.get(id as string), enabled: Boolean(id) });
export const useGlobalSearch = (q: string) => {
  const term = q.trim();
  return useQuery({ queryKey: qk.search(term), queryFn: () => api.search.global(term), enabled: term.length >= 2, ...list, staleTime: 30_000 });
};

// ── platform staff ───────────────────────────────────────────────────────
export const useStaffList = (q: ListQuery<StaffFilters>) => useQuery({ queryKey: qk.staff.list(q), queryFn: () => api.staff.list(q), ...list });
export const useStaff = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.staff.detail(id ?? "none"), queryFn: () => api.staff.get(id as string), enabled: Boolean(id) });

/** Follow-up settings and rules (null: global). */
export const useFollowupSettings = (organizationId: string | null) =>
  useQuery({ queryKey: qk.prompts.followups(organizationId), queryFn: () => api.followups.get(organizationId) });

/** Rogue's prompt, the parts admins edit (null: global). */
export const usePromptSettings = (organizationId: string | null) =>
  useQuery({ queryKey: qk.prompts.settings(organizationId), queryFn: () => api.prompts.get(organizationId) });
